package org.vreo.devicehub.features.device.data.repository

import androidx.compose.ui.graphics.ImageBitmap

import org.vreo.devicehub.features.core.data.appLogger.AppLogger
import org.vreo.devicehub.features.core.presentation.components.byteArrayToBitmap.toImageBitmap
import org.vreo.devicehub.features.core.domain.DataError
import org.vreo.devicehub.features.core.domain.EmptyResult
import org.vreo.devicehub.features.core.domain.Result
import org.vreo.devicehub.features.core.domain.device.videoCameraConfig.VideoConfig
import org.vreo.devicehub.features.core.domain.device.device.AnprDevice
import org.vreo.devicehub.features.core.domain.device.device.networking.RemoteDeviceControl
import org.vreo.devicehub.features.core.domain.device.message.AnprCameraMessage
import org.vreo.devicehub.features.core.domain.device.message.AnprMessageImageRequestDto
import org.vreo.devicehub.features.core.domain.device.message.AnprMessagesRequestDto
import org.vreo.devicehub.features.device.domain.DeviceAccessRepository
import org.vreo.devicehub.features.core.data.device.dto.AnprDeviceRequestDto
import org.vreo.devicehub.features.device.data.network.KtorLocalDeviceControl
import org.vreo.devicehub.features.core.domain.device.device.networking.RemoteDeviceDataSource
import org.vreo.devicehub.features.device.data.network.KtorRemoteMessageDataSource
import org.vreo.devicehub.features.device.data.mappers.toAnprDevice

/**
 * Device access repository impl
 *
 * @property deviceDataSource Provides access to devices data
 * @property messageDataSource Provides access to device's ANPR messages
 * @property deviceControl Provides access to device control methods
 * @property localDeviceAccess Provides access to devices on local network
 */
class DeviceAccessRepositoryImpl(
    val deviceDataSource: RemoteDeviceDataSource,
    val messageDataSource: KtorRemoteMessageDataSource,
    val deviceControl: RemoteDeviceControl,
    val localDeviceAccess: KtorLocalDeviceControl,
) : DeviceAccessRepository {
    /**
     * Get user devices, all the devices linked to user's account
     *
     * @param token user authentication token
     * @return Result of list of devices on success or error on failure
     */
    override suspend fun getUserDevices(
        token: String
    ): Result<List<AnprDevice>, DataError.Remote> {
        return runCatching {
            when (val result = deviceDataSource.getUserDevices(token)) {
                is Result.Success -> {
                    Result.Success(result.data.map { device ->
                        device.toAnprDevice()
                    })
                }

                is Result.Error -> {
                    Result.Error(result.error)
                }
            }
        }.getOrElse {
            AppLogger.error("Failed to get user devices, error: ${it.message}")
            Result.Error(DataError.Remote.UNKNOWN)
        }
    }

    /**
     * Add device to authenticated account using authentication token [token] and provided device
     * data
     *
     * @param token user authentication token
     * @param productNumber product number of device
     * @param serialNumber device serial number
     * @param tag device tag, optional device identifier
     * @param linkingKey linking key/password
     * @return Empty result on success or error on failure
     */
    override suspend fun addDeviceToAccount(
        token: String,
        productNumber: String,
        serialNumber: Long,
        tag: String?,
        linkingKey: String
    ): EmptyResult<DataError.Remote> {
        return runCatching {
            val request = AnprDeviceRequestDto(
                productNumber = productNumber,
                serialNumber = serialNumber,
                tag = tag,
                linkingKey = linkingKey
            )
            deviceDataSource.addDeviceToAccount(token, request)
        }.getOrElse {
            AppLogger.error("Failed to add device to account, error: ${it.message}")
            Result.Error(DataError.Remote.UNKNOWN)
        }
    }

    /**
     * Unlink device from account
     *
     * @param token user authentication token
     * @param deviceId device ID to be unlinked
     * @return Empty result on success or error on failure
     */
    override suspend fun unlinkDeviceFromAccount(
        token: String,
        deviceId: String
    ): EmptyResult<DataError.Remote> {
        return runCatching {
            deviceDataSource.removeDeviceFromAccount(token, deviceId)
        }.getOrElse { error ->
            AppLogger.error("Failed to unlink device from account, error: ${error.message}")
            Result.Error(DataError.Remote.UNKNOWN)
        }
    }

    /**
     * Register new production ready device with all required backend services
     *
     * @param token user authentication token
     * @param productNumber device production number, allows to register device as one of
     * supported device from allowed categories
     * @param serialNumber serial number to be used for target device
     * @return registered device details on success or error on failure
     */
    override suspend fun registerNewDevice(
        token: String,
        productNumber: String,
        serialNumber: Long?,
    ): Result<AnprDevice, DataError.Remote> {
        return runCatching {
            val request = AnprDeviceRequestDto(
                productNumber = productNumber,
                serialNumber = serialNumber ?: 0,
                tag = null,
                linkingKey = ""
            )

            when (val res = deviceDataSource.registerNewProductionDevice(token, request)) {
                is Result.Success -> {
                    Result.Success(res.data.toAnprDevice())
                }

                is Result.Error -> {
                    Result.Error(res.error)
                }
            }

        }.getOrElse { error ->
            AppLogger.error(
                "Failed to register new device: $productNumber, sn: $serialNumber, " +
                        "error: ${error.message}"
            )
            Result.Error(DataError.Remote.UNKNOWN)
        }
    }

    /**
     * Delete device from production environment, this method will remove target device from
     * registered service and device will no longer be able to be accessed or used
     *
     * @param token user authentication token
     * @param deviceId device ID to be deleted
     * @return Empty result on success or error on failure
     */
    override suspend fun deleteDeviceFromProduction(
        token: String,
        deviceId: String
    ): EmptyResult<DataError.Remote> {
        return runCatching {
            deviceDataSource.deleteDeviceFromProduction(token, deviceId)
        }.getOrElse { error ->
            AppLogger.error("Failed to delete device from production, error: ${error.message}")
            Result.Error(DataError.Remote.UNKNOWN)
        }
    }

    /**
     * Get device production register (xlsx file). File includes details of all devices
     * registered in production database
     *
     * @param token user authentication token
     * @return Result of device production register on success or error on failure
     */
    override suspend fun getDeviceProductionRegister(
        token: String
    ): Result<ByteArray, DataError.Remote> {
        return runCatching {
            deviceDataSource.getDeviceDetailsFile(token)
        }.getOrElse {
            AppLogger.error("Failed to get device production register, error: ${it.message}")
            Result.Error(DataError.Remote.UNKNOWN)
        }
    }

    /**
     * Get messages for selected device ID
     *
     * @param token user authentication token
     * @param deviceId ID of the device to access
     * @param limit maximum number of messages to be retrieved
     * @param fromTime unix timestamp in seconds of from when to retrieve message
     * @param toTime unix timestamp in seconds of to when to retrieve messages
     * @return Result of list of messages on success or error on failure
     */
    override suspend fun getMessages(
        token: String,
        deviceId: String,
        limit: Int,
        fromTime: Long?,
        toTime: Long?,
        ignoreIds: List<String>?
    ): Result<List<AnprCameraMessage>, DataError.Remote> {
        return runCatching {
            val request = AnprMessagesRequestDto(
                deviceId = deviceId,
                limit = limit,
                fromTime = fromTime,
                toTime = toTime,
            )

            when (val res = messageDataSource.getAnprMessages(token, request)) {
                is Result.Success -> {
                    Result.Success(res.data)
                }

                is Result.Error -> {
                    Result.Error(res.error)
                }
            }
        }.getOrElse { error ->
            AppLogger.error("Failed to get messages, error: ${error.message}")
            Result.Error(DataError.Remote.UNKNOWN)
        }
    }

    /**
     * Get image from remote data source
     *
     * @param token user authentication token
     * @param path path to image, this should be acquired from the [AnprCameraMessage]
     * @return Result of image on success or error on failure
     */
    override suspend fun getImage(
        token: String,
        path: String,
    ): Result<ImageBitmap?, DataError.Remote> {
        return runCatching {
            when (val res = messageDataSource.getImage(token, AnprMessageImageRequestDto(path))) {
                is Result.Success -> {
                    Result.Success(res.data.toImageBitmap())
                }

                is Result.Error -> {
                    Result.Error(res.error)
                }
            }
        }.getOrElse {
            AppLogger.error("Failed to get image, error: ${it.message}")
            Result.Error(DataError.Remote.UNKNOWN)
        }
    }

    /**
     * Get device's video configuration
     *
     * @param token user authentication token
     * @param deviceId device ID
     * @return Result of video configuration on success or error on failure
     */
    override suspend fun getVideoConfiguration(
        token: String,
        deviceId: String
    ): Result<VideoConfig, DataError.Remote> {
        return deviceControl.getVideoConfig(token, deviceId)
    }

    /**
     * Set device's video configuration
     *
     * @param token user authentication token
     * @param deviceId device ID
     * @param config video configuration to be sent to device
     * @return Result of empty on success or error on failure
     */
    override suspend fun setVideoConfiguration(
        token: String,
        deviceId: String,
        config: VideoConfig
    ): EmptyResult<DataError.Remote> {
        return deviceControl.setVideoConfig(
            token = token,
            deviceId = deviceId,
            config = config
        )
    }

    /**
     * Connect to device on local network
     *
     * @param deviceIpAddress device ip address
     * @param devicePort device port
     * @param password device password
     * @return Result of empty on success or error on failure
     */
    override suspend fun connectToLocalDevice(
        deviceIpAddress: String,
        devicePort: String,
        password: String
    ): EmptyResult<DataError.Remote> {
        return localDeviceAccess.pingDevice(
            deviceIpAddress = "$deviceIpAddress:$devicePort",
            password = password
        )
    }

    /**
     * Apply device configuration. Send configuration to device on local network.
     *
     * @param deviceIpAddress device ip address
     * @param device device configuration
     * @param devicePort device port
     * @param password device password
     * @return Result of empty on success or error on failure
     */
    override suspend fun applyDeviceConfiguration(
        deviceIpAddress: String,
        device: AnprDevice,
        devicePort: String,
        password: String
    ): EmptyResult<DataError.Remote> {
        return localDeviceAccess.applyDeviceConfig(
            deviceIpAddress = "$deviceIpAddress:$devicePort",
            password = password,
            device = device
        )
    }
}